/**
 * Per-section header STATUS (dashboard-flow-ux.md 6C / session 97).
 *
 * A small pure derivation that turns the already-computed section signals
 * (`isSectionNA`, the shared `sectionProgress`, and "has any user data") into the
 * single status tag rendered on the collapsed accordion header:
 *
 *   complete | attention | not_started | na | no_status
 *
 * It deliberately does NOT recompute completeness — `progress.complete` is the SAME
 * boolean that drives the green check (G7: one completion source, never forked). The
 * status tag supplements the check, it does not redefine "done".
 *
 * `no_status` (no tag) is reserved for sections with NO completion criterion — i.e.
 * `sectionProgress` returned `null` (no required field / hidden). An empty section
 * that DOES have a criterion is `not_started`, not `no_status` — the distinction is
 * "nothing to measure" vs "measurable but untouched".
 */
import type { SectionProgressResult } from "@shared/section-progress";

export type SectionStatus = "complete" | "attention" | "not_started" | "na" | "no_status";

export interface SectionStatusResult {
  status: SectionStatus;
  /** For `attention` only: outstanding items (`total - filled`, floored at 1). 0 otherwise. */
  count: number;
}

export interface SectionStatusInput {
  /** Section is N/A — auto-gate flag `=== false` OR manual `client_section_na`. */
  isNA: boolean;
  /** Shared section progress, or `null` when there is no completion criterion. */
  progress: SectionProgressResult;
  /** True when the section holds ≥1 user-entered (non-seeded) entry. */
  hasUserData: boolean;
}

export function computeSectionStatus(input: SectionStatusInput): SectionStatusResult {
  if (input.isNA) return { status: "na", count: 0 };
  const p = input.progress;
  // No completion criterion (no required field / hidden) → no tag.
  if (!p) return { status: "no_status", count: 0 };
  // Complete uses the SAME boolean that drives the green check (G7 single source).
  if (p.complete) return { status: "complete", count: 0 };
  // Has a criterion but no real user data yet → not started.
  if (!input.hasUserData) return { status: "not_started", count: 0 };
  // Touched but unfinished → needs attention. Count outstanding items.
  return { status: "attention", count: Math.max(1, p.total - p.filled) };
}

// ── Collapsed-row single badge (s99, D-99.1) ──────────────────────────────────
// ONE status per collapsed row, by priority. This is a PRESENTATION refinement of
// `computeSectionStatus` — it buckets the SAME shared `filled`/`total` numbers into
// five display states; it never recomputes completeness (G7). The "complete" status
// (≥1 complete row / all required present) splits into:
//   • complete    — filled === total (all touched rows / required fields done)
//   • in_progress — 0 < filled < total (some done, others still in progress)
// `attention` (touched, nothing complete) and `not_started` / `na` / `none` carry
// straight through. The dashboard/essentials still read the unchanged shared
// computation; this only governs the header chip.
export type HeaderBadgeKind =
  | "complete"
  | "in_progress"
  | "attention"
  | "not_started"
  | "na"
  | "none";

export interface HeaderBadge {
  kind: HeaderBadgeKind;
  /** Display text, or `null` for `none` (render nothing). */
  label: string | null;
}

export function sectionHeaderBadge(
  status: SectionStatusResult,
  progress: SectionProgressResult,
): HeaderBadge {
  switch (status.status) {
    case "na":
      return { kind: "na", label: "Doesn't apply" };
    case "no_status":
      return { kind: "none", label: null };
    case "not_started":
      // s177 (A2, D-177.5b): "Nothing recorded yet" replaces "Not started". After the
      // suggestion-row retirement an empty section is the NORMAL opening state, not a task
      // the planner has neglected — "Not started" reads as a scolding for a list that was
      // never theirs to begin with. It also states the honest fact: nothing is recorded.
      return { kind: "not_started", label: "Nothing recorded yet" };
    case "attention": {
      const n = status.count > 0 ? status.count : 1;
      return { kind: "attention", label: n === 1 ? "1 need attention" : `${n} need attention` };
    }
    case "complete": {
      // `complete` ⟺ filled > 0. Split on whether every measured unit is done.
      if (progress && progress.total > 1 && progress.filled < progress.total) {
        return { kind: "in_progress", label: `${progress.filled} of ${progress.total}` };
      }
      return { kind: "complete", label: "Complete" };
    }
  }
}

/** Tag label for a status result. `null` when no tag should render (`no_status`). */
export function sectionStatusLabel(result: SectionStatusResult): string | null {
  switch (result.status) {
    case "complete":
      return "Complete";
    case "attention":
      return result.count === 1 ? "1 item needs attention" : `${result.count} items need attention`;
    case "not_started":
      // s177 (A2, D-177.5b) — see the note in sectionHeaderBadge; same words, same reason.
      return "Nothing recorded yet";
    case "na":
      return "Doesn't apply";
    case "no_status":
      return null;
  }
}
